import React, { useContext } from 'react'

import PlannedHotelCtaContent from './Hotel/PlannedHotelCtaContent'
import ItineraryItemCard from './ItineraryItemCard'
import { PlannedItemPriceContent } from './PlannedItemPriceContent'
import { ItemCardVariant } from './types'
import BookmarkLocation from '../Bookmark/BookmarkCardNew/BookmarkLocation'
import BedbankOfferPriceDetails from '../ItemOfferPrice/BedbankOfferPriceDetails'
import TripSummaryCardLoadingSkeleton from '../TripSummary/TripSummaryCardLoadingSkeleton'

import OfferLabels from 'components/Common/Labels/OfferLabels'
import TextButton from 'components/Luxkit/Button/TextButton'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import OfferTileInclusions from 'components/OfferList/OfferListTiles/Support/OfferTileInclusions'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import { isLEHotel } from 'lib/offer/offerTypes'
import { isLuxuryPlusEnabled } from 'loyalty/selectors/featureToggle'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import LEOfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/LEOfferPriceDetails'
import SavedOfferCard from 'tripPlanner/components/SavedOffers/SavedOfferCard'
import { VIEW_OFFER_COPY } from 'tripPlanner/config'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useOffer } from 'tripPlanner/hooks/api'
import useAccommodationItemConfirmationCodes from 'tripPlanner/hooks/confirmationCodes/useAccommodationItemConfirmationCodes'
import useAccommodationItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useAccommodationItemOccupancyDescription'
import useAccommodationOfferAvailable from 'tripPlanner/hooks/useAccommodationOfferAvailable'
import { HotelItem } from 'tripPlanner/types/tripItem'
import {
  formatShortDateRange,
  getOfferImage,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { extractPriceAndValue } from 'tripPlanner/utils/bookmarkCard'
import { getAccommodationPriceAndLabel } from 'tripPlanner/utils/hotel'
import { findHotelPackage } from 'tripPlanner/utils/offer'

interface Props {
  item: HotelItem
  tripNameLabel?: React.ReactNode
  variant: ItemCardVariant
}

function HotelItemCard({ item, tripNameLabel, variant }: Props) {
  // The query type includes hotels and tours but this card is only used for hotels
  const { data: offer, isLoading: _isLoading } = useOffer(item.code)
  const isLoading = !!(item.code && _isLoading) // Don't show loading state if there's no offer to load
  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate

  const isLuxPlusEnabled = useAppSelector(isLuxuryPlusEnabled)

  const { id: imgId, url: imgSrc } = getOfferImage(offer)

  const productTypeLabel = offer ? (
    <ProductTypeLabel productType={offer.productType} />
  ) : null

  let priceAreaContent: JSX.Element | null = null
  let ctaAreaContent: JSX.Element | null = null
  let urgencyLabels: JSX.Element | null = null

  const roomDescription = item.savedItemData?.roomTypeName

  const isOfferAvailable = useAccommodationOfferAvailable(
    offer,
    item.savedItemData,
    item.code,
  )
  let pkg: App.Package | undefined = undefined
  if (offer && isLEHotel(offer)) {
    const packageId = item.savedItemData?.packageId
    if (packageId) {
      pkg = offer.packages.find((p) => p.id === packageId)
    }
    if (!pkg) {
      pkg = offer.lowestPricePackage
    }
  }

  const timeDescription = formatShortDateRange(item)
  const confirmation = useAccommodationItemConfirmationCodes(item)
  const { occupanciesText, numberOfRoomsText } =
    useAccommodationItemOccupancyDescription(item)
  const timeAndOccupancyText = nonNullable([timeDescription, occupanciesText])
    .filter((text) => text.length > 0)
    .join(', ')

  if (isLoading) {
    return <TripSummaryCardLoadingSkeleton />
  }

  if (offer && isLEHotel(offer)) {
    urgencyLabels = <OfferLabels offer={offer} />
  }
  // Whole purpose of this series of conditionals is to set the 'bottomContent' correctly
  // If nothing gets set, it's a booking or custom item
  if (isLeBookmark(item) && offer && offer.parentType === 'hotel') {
    if (viewAsTemplate) {
      if (offer.type === 'bedbank_hotel') {
        const { checkIn, checkOut } = item.savedItemData
        priceAreaContent =
          checkIn && checkOut ? (
            <BedbankOfferPriceDetails
              offer={offer}
              roomId={item.subCode}
              checkIn={checkIn}
              checkOut={checkOut}
            />
          ) : null
      } else {
        if (isLEHotel(offer) && offer.lowestPricePackage) {
          const { price, luxPlusPrice } = extractPriceAndValue({
            pkg: offer.lowestPricePackage,
          })
          const saveAmount =
            price && luxPlusPrice && luxPlusPrice < price ?
              price - luxPlusPrice :
              undefined
          urgencyLabels = (
            <OfferLabels
              offer={offer}
              saveAmount={saveAmount}
              showMemberInclusionLabels
            />
          )
        }
        priceAreaContent = (
          <LEOfferPriceDetails
            offer={offer as App.Offer}
            package={findHotelPackage(offer.packages, item)}
          />
        )
      }
      const offerLink = offerPageURL(offer)
      ctaAreaContent = (
        <Group direction="horizontal" gap={12}>
          <SaveTripItemButton item={item} />
          <TextButton kind="primary" to={offerLink}>
            {VIEW_OFFER_COPY}
          </TextButton>
        </Group>
      )
    } else {
      const { discountPercent, currencyCode } = item.savedItemData

      const { price, luxPlusPrice, priceLabel } = getAccommodationPriceAndLabel(
        offer,
        item.savedItemData,
      )

      if (isLEHotel(offer) && isLuxPlusEnabled) {
        const saveAmount =
          price && luxPlusPrice && luxPlusPrice < price ?
            price - luxPlusPrice :
            undefined
        urgencyLabels = <OfferLabels offer={offer} saveAmount={saveAmount} />
      }

      priceAreaContent = (
        <PlannedItemPriceContent
          discountPercent={discountPercent}
          price={price}
          luxPlusPrice={luxPlusPrice}
          priceLabel={priceLabel}
          currencyCode={currencyCode}
          isOfferAvailable={isOfferAvailable}
        />
      )

      const offerLink = buildProductLink(item, !viewAsTemplate, { offer }) ?? ''
      ctaAreaContent = (
        <PlannedHotelCtaContent
          offerLink={offerLink}
          item={item}
          isOfferAvailable={isOfferAvailable}
          variant={variant}
        />
      )
    }
  }

  const placeId = 'id' in item.place ? item.place.id : undefined

  if (isLeBookmark(item) && offer) {
    const locationDescription = (
      <BookmarkLocation
        variant="single"
        startLocation={item.place.region}
        showIcon={false}
      />
    )
    let inclusions: Array<App.TileInclusion> = EmptyArray

    if (isLEHotel(offer)) {
      if (offer.lowestPricePackage?.inclusionsV2) {
        inclusions = offer.lowestPricePackage.inclusionsV2.map((inclusion) => ({
          text: inclusion.inclusion.name,
          isHighlighted: false,
          icon: inclusion.inclusion.category?.icon,
          isBonus: inclusion.style === 'bonus',
        }))
      }
      if (inclusions.length === 0 && offer.tileInclusions) {
        inclusions = offer.tileInclusions
      }
    }
    let rating: number | undefined
    let numberOfReviews: number | undefined
    if ('property' in offer && offer.property) {
      rating = offer.property.rating
      numberOfReviews = offer.property.reviewsTotal
    }
    const offerLink = buildProductLink(item, !viewAsTemplate, { offer }) ?? ''
    return (
      <SavedOfferCard
        item={item}
        name={item.place.name}
        imageOverlayContent={productTypeLabel}
        imgSrc={imgSrc}
        imgId={imgId}
        locationDescription={locationDescription}
        descriptors={[timeAndOccupancyText, numberOfRoomsText, roomDescription]}
        placeId={placeId}
        priceAreaContent={priceAreaContent}
        ctaAreaContent={ctaAreaContent}
        urgencyLabels={urgencyLabels}
        offerLink={offerLink}
        isSoldOut={!isOfferAvailable}
        testId="saved-hotel-card"
        tripNameLabel={tripNameLabel}
        variant={variant}
        inclusions={
          inclusions.length > 0 && (
            <OfferTileInclusions
              heading=""
              offerTitle={item.place.name}
              tileInclusionsList={inclusions}
              maxInclusionsCount={2}
              offerURL={offerLink}
            />
          )
        }
        rating={rating}
        numberOfReviews={numberOfReviews}
      />
    )
  } else {
    // Custom Item Card OR Booking

    return (
      <ItineraryItemCard
        item={item}
        name={item.place.name}
        imgSrc={imgSrc}
        imgId={imgId}
        locationDescription={item.place.region}
        placeId={placeId}
        descriptors={[timeDescription, numberOfRoomsText, roomDescription]}
        address={item.place.address}
        confirmationCode={confirmation.codeString}
        confirmationCodeLabel={confirmation.label}
        imageOverlayContent={productTypeLabel}
        tripNameLabel={tripNameLabel}
        variant={variant}
      />
    )
  }
}

export default HotelItemCard
